import { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18next';
import clsx from 'clsx';
import { Store } from 'antd/es/form/interface';

import { useConfirmModal } from 'utils/customHooks/useConfirmModal';

import { OpButton } from '../OpButton/OpButton';
import { OpTooltip } from '../OpTooltip/OpTooltip';
import { OpFlex } from '../OpFlex/OpFlex';
import { useOpFormDrawerContext } from '../OpFormDrawer/OpFormDrawerContext';

import { OpForm } from './OpForm';
import { useOpFormContext } from './OpFormContext';

import './FormButtons.scss';

export interface FormButtonsProps<T extends Store> {
  className?: string;
  form: ComponentProps<typeof OpForm<T>>['form'];
  isFormLoading?: boolean;
  isSubmitButtonLoading?: boolean;
  submitButtonLabel?: string;
  resetButtonLabel?: string;
  isReadOnly?: boolean;
  tooltip?: string;
  gtm?: string;
  onResetClick?: () => void;
  leftContent?: ReactNode;
  isSubmitButtonDisabled?: boolean;
}

/**
 * FormButtons is an internal component used by OpForm to render submit and reset buttons.
 * It provides consistent button styling and behavior for form actions with confirmation dialogs.
 *
 * This component is primarily for internal use within OpForm. It manages form submission states,
 * handles reset confirmations when there are unsaved changes, and integrates with form drawer context.
 */
export const FormButtons = <T extends Store = Store>({
  className,
  form,
  isFormLoading = false,
  isSubmitButtonLoading = false,
  submitButtonLabel = translate('Save'),
  resetButtonLabel = translate('Reset'),
  isReadOnly = false,
  tooltip,
  gtm,
  onResetClick,
  leftContent,
  isSubmitButtonDisabled = false,
}: FormButtonsProps<T>) => {
  const confirmModal = useConfirmModal();
  const { t } = useTranslation();
  const { isSubmitting: isDrawerFormSubmitting } = useOpFormDrawerContext();
  const { isSubmitting: isCoreFormSubmitting } = useOpFormContext();

  const isSubmitting = isDrawerFormSubmitting || isCoreFormSubmitting;

  const handleResetClick = () => {
    if (form?.isFieldsTouched()) {
      confirmModal({
        title: t('Confirm discard?'),
        content: t(
          'You have unsaved changes. Are you sure you wish to discard them?',
        ),
        cancelText: t('No'),
        closable: true,
        okText: t('Yes'),
        onOk: () => {
          form.resetFields();
          onResetClick?.();
        },
      });
      return;
    }

    onResetClick?.();
  };

  const onSubmitClick = () => {
    form?.submit();
  };

  return (
    <OpTooltip title={tooltip} placement="topRight">
      <OpFlex
        className={clsx('form-buttons', className)}
        justify="end"
        align="center"
        gap="middle"
      >
        {leftContent && (
          <>
            {leftContent}
            {/* Spacer is required to "push" the left content to start of flexbox */}
            <div className="form-buttons__spacer" />
          </>
        )}

        <OpButton
          gtm={gtm && `${gtm}-reset-form-button`}
          testId="reset-form-button"
          onClick={handleResetClick}
          disabled={isReadOnly || isFormLoading || isSubmitting}
        >
          {resetButtonLabel}
        </OpButton>
        <OpButton
          gtm={gtm && `${gtm}-submit-form-button`}
          testId="submit-form-button"
          type="primary"
          onClick={onSubmitClick}
          disabled={isReadOnly || isFormLoading || isSubmitButtonDisabled}
          loading={isSubmitting || isSubmitButtonLoading}
        >
          {submitButtonLabel}
        </OpButton>
      </OpFlex>
    </OpTooltip>
  );
};
